import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import localidadService from '../../services/localidadService';
import * as XLSX from 'xlsx';

interface LocalidadPagedResult {
  items: any[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const Localidades: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [localidadesData, setLocalidadesData] = useState<LocalidadPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);

  const getProvinciaDisplay = (localidad: any) =>
    localidad?.nombreProvincia?.trim() ||
    localidad?.provincia?.nombreProvincia?.trim() ||
    (localidad?.codigoProvincia ?? '-');

  const fetchLocalidades = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const response = await localidadService.getLocalidades({
        pageNumber,
        pageSize,
        searchTerm: search || undefined
      });
      setLocalidadesData(response);
    } catch (error: any) {
      console.error('Error al cargar localidades:', error);
      enqueueSnackbar('Error al cargar localidades', { variant: 'error' });
      setLocalidadesData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchLocalidades(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (codigoCiudad: number) => {
    if (window.confirm('¿Está seguro de eliminar esta localidad?')) {
      try {
        await localidadService.deleteLocalidad(codigoCiudad);
        enqueueSnackbar('Localidad eliminada exitosamente', { variant: 'success' });
        fetchLocalidades(page + 1, rowsPerPage, searchTerm);
      } catch (error: any) {
        console.error('Error al eliminar localidad:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar localidad', { variant: 'error' });
      }
    }
  };

  const handleExportExcel = () => {
    const dataToExport = localidadesData.items.map(l => ({
      'Código': l.codigoCiudad,
      'Nombre': l.nombreCiudad,
      'Provincia': getProvinciaDisplay(l),
      'Código Postal': l.codigoPostal
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Localidades');
    XLSX.writeFile(wb, 'Localidades.xlsx');
    enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
  };

  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        let successCount = 0;
        let errorCount = 0;

        for (const row of jsonData as any[]) {
          try {
            const localidad = {
              codigoCiudad: Number(row['Código'] || row['codigoCiudad']),
              nombreCiudad: row['Nombre'] || row['nombreCiudad'],
              codigoProvincia: Number(row['Provincia'] || row['codigoProvincia']),
              codigoPostal: row['Código Postal'] || row['codigoPostal'] || ''
            };

            await localidadService.createLocalidad(localidad);
            successCount++;
          } catch (error) {
            errorCount++;
            console.error('Error al importar localidad:', error);
          }
        }

        enqueueSnackbar(`Importación completada: ${successCount} exitosos, ${errorCount} errores`, { 
          variant: errorCount > 0 ? 'warning' : 'success' 
        });
        fetchLocalidades(page + 1, rowsPerPage, searchTerm);
      } catch (error) {
        enqueueSnackbar('Error al procesar el archivo Excel', { variant: 'error' });
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Localidades
            </Typography>
            <Stack direction="row" spacing={1}>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="import-excel-localidades"
                type="file"
                onChange={handleImportExcel}
              />
              <label htmlFor="import-excel-localidades">
                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small">
                  Importar
                </Button>
              </label>
              <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleExportExcel} size="small">
                Exportar
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/sistema/localidades/nuevo')}>
                Nueva Localidad
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {localidadesData.items.map((localidad) => (
                <Card key={localidad.codigoCiudad} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {localidad.codigoCiudad} - {localidad.nombreCiudad}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CP: {localidad.codigoPostal || '-'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/sistema/localidades/${localidad.codigoCiudad}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(localidad.codigoCiudad)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Provincia</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Código Postal</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {localidadesData.items.map((localidad) => (
                    <TableRow key={localidad.codigoCiudad} hover>
                      <TableCell>{localidad.codigoCiudad}</TableCell>
                      <TableCell>{localidad.nombreCiudad}</TableCell>
                      <TableCell>{getProvinciaDisplay(localidad)}</TableCell>
                      <TableCell>{localidad.codigoPostal || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/sistema/localidades/${localidad.codigoCiudad}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(localidad.codigoCiudad)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={localidadesData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Localidades;
